import * as React from "react";
import {infoIcons} from "@vebox/etui-core";
import {CensusCoolerContext} from "../../providers/CensusCollerProvider";
import {createColumnsList, createDatasource} from "./utils";
import {FindEquipmentContext} from "../../providers/FindEquipmentProvider";
import {uuid} from "../../../../../types/veboxModels";
import {EmptyState} from "@vebox/etui-core/EmptyState";
import {InfiniteScrollTable} from "../../components/InfiniteScrollTable";

export const FindEquipmentTable: React.FC = () => {
    const {getEquipmentTableHeadersName, AppTranslations} =
        React.useContext(CensusCoolerContext);
    const {
        equipmentList,
        equipmentGuidsList,
        selectedEquipment,
        updateSelectedEquipment,
        updateAreAllSelected,
        areAllSelected,
    } = React.useContext(FindEquipmentContext);

    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const [hasNextPage, setHasNextPage] = React.useState<boolean>(true);
    const [currentPage, setCurrentPage] = React.useState(0);

    const removeAddSelectedEquipment = (equipmentGuid: uuid) => (event) => {
        const value = event.target.checked;
        if (value) {
            updateSelectedEquipment([...selectedEquipment, equipmentGuid]);
        } else {
            updateSelectedEquipment(
                selectedEquipment.filter((guid) => guid !== equipmentGuid),
            );
        }
    };

    const removeAddAllSelectedEquipment = (event) => {
        const value = event.target.checked;
        if (value) {
            updateSelectedEquipment(equipmentGuidsList?.data);
        } else {
            updateSelectedEquipment([]);
        }
    };

    const onLoadNextPage = (): Promise<void> => {
        const newPage = currentPage + 1;
        setCurrentPage(newPage);
        return equipmentList?.handleOnLoadNextPage(newPage);
    };

    React.useEffect(() => {
        if (areAllSelected) {
            updateSelectedEquipment(equipmentGuidsList?.data);
        }
    }, [equipmentGuidsList?.data]);

    React.useEffect(() => {
        setIsLoading(equipmentList?.isFetching || equipmentGuidsList?.isLoading);
    }, [equipmentList?.isFetching, equipmentGuidsList?.isLoading]);

    const list = React.useMemo(() => {
        const selectedAll =
            selectedEquipment?.length === equipmentGuidsList?.data?.length;

        updateAreAllSelected(selectedAll);
        const list = createDatasource(
            equipmentList?.flattenedData ?? [],
            removeAddSelectedEquipment,
            selectedEquipment ?? [],
            selectedAll,
        );
        setHasNextPage(equipmentList?.flattenedData.length !== equipmentList?.totalCount);
        return list;
    }, [
        equipmentList?.flattenedData,
        selectedEquipment,
        equipmentList?.isNextPageLoading,
        equipmentGuidsList?.data,
    ]);

    return (
        <>
            <h2 className={"find-equipment-list-count"}>
                {!isLoading ? (selectedEquipment?.length ?? 0) : 0}/
                {!isLoading ? (equipmentGuidsList?.data?.length ?? 0) : 0}{" "}
                {AppTranslations.massiveActions.censusCooler.selectedEquipment}
            </h2>
            <div className={"find-equipment-list-table-container"}>
                <InfiniteScrollTable
                    data={list}
                    columns={createColumnsList(
                        getEquipmentTableHeadersName,
                        removeAddAllSelectedEquipment,
                        areAllSelected,
                    )}
                    update={onLoadNextPage}
                    hasNextPage={hasNextPage}
                    isLoading={isLoading}
                    height={"calc(70vh - 100px)"}
                    emptyPlaceholder={
                        <EmptyState>
                            <EmptyState.InfoIcon
                                styles={{
                                    root: {
                                        color: "var(--empty-indicator-svg-stroke)",
                                    },
                                }}
                                glyph={infoIcons.NoContent}
                            />
                            <EmptyState.Title color="var(--empty-indicator-title-color)">
                                {AppTranslations.noDataAvailable}
                            </EmptyState.Title>
                        </EmptyState>
                    }
                />
            </div>
        </>
    );
};
